import { useMemo } from 'react';
import { useLeadDepositRanking } from '@/hooks/queries';
import { Avatar, CardSkeleton, ErrorState, SectionHead, StatLine, TableSkeleton } from '@/components/ui';
import { GrowthTag } from '@/components/GrowthTag';
import { IconDownload } from '@/components/Icons';
import { formatBRL, formatBRLShort, formatISODateTime, formatNumber, formatPercent } from '@/lib/format';
import type { LeadDepositEntry, LeadDepositRanking as Ranking } from '@/lib/types';
import {
  averagePerDeposit, depositQuery, depositsLabel, DepositToolbar, NoDeposits, previousPeriodLabel, typeSuffix, UnlinkedStat,
  type DepositFilters,
} from './DepositToolbar';
import { LeaderboardList, Podium, splitPodium, type BoardItem } from './Leaderboard';
import { PositionDelta } from './PositionDelta';
import { downloadLeadDepositCsv } from './leadDepositCsv';

/** O maior número de clientes que a API devolve numa classificação. */
const LIMIT = 1000;

const itemOf = (entry: LeadDepositEntry): BoardItem => ({
  id: entry.lead.id,
  position: entry.position,
  name: entry.lead.name,
  avatarColor: entry.lead.avatarColor,
  photoUrl: null,
  score: entry.depositTotal,
  scoreLabel: formatBRL(entry.depositTotal),
  // O responsável por último: numa linha estreita, é ele que o corte encurta.
  details: [
    depositsLabel(entry.depositCount),
    `${formatPercent(entry.share, 1)} do depositado`,
    entry.owner ? `responsável ${entry.owner.name}` : 'sem responsável',
  ],
  growth: entry.growth,
  previousLabel: formatBRL(entry.previousTotal),
  positionChange: entry.positionChange,
});

interface Props {
  filters: DepositFilters;
  onChange: (patch: Partial<DepositFilters>) => void;
}

/**
 * Os clientes pelo volume depositado no período — a contabilização do leilão,
 * no mesmo pódio do ranking de membros. Soma os comprovantes de depósito
 * vinculados a cada cliente; o saldo informado no cliente fica na tabela, só
 * como referência. Só o administrador chega aqui (pages/RankingPage.tsx; a
 * API também recusa o membro).
 */
export function LeadDepositRanking({ filters, onChange }: Props) {
  const { data, isLoading, error } = useLeadDepositRanking({
    ...depositQuery(filters),
    memberId: filters.memberId || undefined,
    limit: LIMIT,
  });
  const items = useMemo(() => (data?.entries ?? []).map(itemOf), [data]);

  if (error) return <ErrorState message={(error as Error).message} />;

  const { podium, rest } = splitPodium(items);

  return (
    <>
      <DepositToolbar
        filters={filters}
        onChange={onChange}
        withMember
        actions={(
          <button type="button" className="btn" disabled={!data || data.entries.length === 0} onClick={() => data && downloadLeadDepositCsv(data)}>
            <IconDownload /> Baixar CSV
          </button>
        )}
      />

      {isLoading || !data ? (
        filters.view === 'table' ? <TableSkeleton rows={8} columns={8} /> : (
          <div className="grid grid3"><CardSkeleton height={220} /><CardSkeleton height={260} /><CardSkeleton height={200} /></div>
        )
      ) : (
        <>
          <Totals data={data} />
          <TruncatedNote data={data} />
          {podium.length === 0 ? (
            <NoDeposits label={data.period.label} period={filters.period} onPeriod={(period) => onChange({ period })} />
          ) : filters.view === 'table' ? (
            <DepositTable entries={data.entries} />
          ) : (
            <>
              <Podium items={podium} previousPeriod={previousPeriodLabel(data)} />
              {rest.length > 0 ? (
                <section>
                  <SectionHead title={`Classificação · ${formatNumber(items.length)} ${items.length === 1 ? 'cliente' : 'clientes'}`} />
                  <LeaderboardList items={rest} leaderScore={podium[0]?.score ?? 0} previousPeriod={previousPeriodLabel(data)} />
                </section>
              ) : null}
            </>
          )}
        </>
      )}
    </>
  );
}

function Totals({ data }: { data: Ranking }) {
  const { totals } = data;
  return (
    <div className="metric-row" style={{ marginBottom: 34 }}>
      <StatLine
        label={`Depositado pelos clientes · ${data.period.label}${typeSuffix(data.type)}`}
        value={formatBRLShort(totals.depositTotal)}
        color="var(--emphasis)"
        sub={<GrowthTag growth={totals.growth} previousLabel={formatBRL(totals.previousDepositTotal)} />}
      />
      <StatLine
        label="Depósitos"
        value={formatNumber(totals.depositCount)}
        sub={`Média ${formatBRLShort(averagePerDeposit(totals.depositTotal, totals.depositCount))} por depósito`}
      />
      <StatLine label="Clientes" value={formatNumber(totals.leadCount)} sub={`Média ${formatBRLShort(totals.averagePerLead)} por cliente`} />
      <UnlinkedStat total={totals.unlinkedTotal} count={totals.unlinkedCount} />
    </div>
  );
}

/** O corte da lista: a conta do leilão precisa saber que tem mais gente abaixo. */
function TruncatedNote({ data }: { data: Ranking }) {
  if (!data.truncated) return null;
  return (
    <p className="muted" style={{ fontSize: 12.5, margin: '0 0 16px' }}>
      Mostrando os {formatNumber(data.entries.length)} primeiros de {formatNumber(data.totals.leadCount)} clientes. Encurte o período para ver o resto.
    </p>
  );
}

function DepositTable({ entries }: { entries: LeadDepositEntry[] }) {
  // Saldo informado é opcional no cliente: sem nenhum na lista, a coluna seria só traços.
  const withBalance = entries.some((entry) => entry.balance > 0);
  return (
    <div className="table-wrap">
      <table className="tbl tbl-cards">
        <thead>
          <tr>
            <th style={{ width: 52 }}>#</th>
            <th>Cliente</th>
            <th>Responsável</th>
            <th className="right">Depositado</th>
            <th className="right">Depósitos</th>
            <th className="right">Maior depósito</th>
            <th className="nowrap">Último depósito</th>
            <th className="right">Part.</th>
            {withBalance ? (
              <th className="right" title="O saldo que o membro informou no cliente: a posição de hoje, não do período">Saldo informado</th>
            ) : null}
            <th className="right">vs. anterior</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((entry) => (
            <tr key={entry.lead.id} className="row-hover rank-row" data-place={entry.position}>
              <td className="cell-pos">
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  <span className="num display rank-pos">{entry.position}</span>
                  <PositionDelta change={entry.positionChange} />
                </span>
              </td>
              <td className="cell-main">
                <div style={{ fontWeight: 500 }}>{entry.lead.name}</div>
                {entry.lead.contact ? (
                  <div className="muted truncate" style={{ fontSize: 11.5, maxWidth: 220 }} title={entry.lead.contact}>{entry.lead.contact}</div>
                ) : null}
              </td>
              <td data-label="Responsável">
                {entry.owner ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                    <Avatar name={entry.owner.name} color={entry.owner.avatarColor} photo={entry.owner.photoUrl} size={22} />
                    <span className="nowrap">{entry.owner.name}</span>
                  </span>
                ) : (
                  <span className="muted nowrap">Sem responsável</span>
                )}
              </td>
              {/* Valores inteiros, com centavos: é a conta do leilão. No celular, rótulos curtos para caber no cartão. */}
              <td className="num right emphasis cell-key" style={{ fontWeight: 500 }} data-label="Depositado">{formatBRL(entry.depositTotal)}</td>
              <td className="num right cell-third" data-label="Depósitos">{formatNumber(entry.depositCount)}</td>
              <td className="num right cell-third" data-label="Maior">{formatBRL(entry.largestDeposit)}</td>
              <td className="num nowrap cell-third" data-label="Último">{formatISODateTime(entry.lastDepositAt)}</td>
              <td className="num right muted cell-third" data-label="Part.">{formatPercent(entry.share, 1)}</td>
              {withBalance ? (
                <td className="num right muted cell-third" data-label="Saldo hoje">
                  {entry.balance > 0 ? formatBRL(entry.balance) : '—'}
                </td>
              ) : null}
              <td className="right cell-third" data-label="vs. anterior">
                <GrowthTag growth={entry.growth} previousLabel={formatBRL(entry.previousTotal)} compact />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
